import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useSchoolTimezone } from '@/contexts/TimezoneContext';
import { isAdminSite } from '@/utils/environment';
import { applyBranding } from '@/utils/brandingUtils';
import { LazyLoadFallback } from '@/components/LazyLoadFallback';
import { LazyLoadErrorBoundary } from '@/components/LazyLoadErrorBoundary';
import { FeatureModuleGuard, ModuleNotAvailable } from '@/components/FeatureModuleGuard';

// ============================================
// CRITICAL PAGES - Eager Loaded (Fast Initial Load)
// ============================================
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import LoginPage from '@/pages/LoginPage';
import NotFoundPage from '@/pages/NotFoundPage';
import DashboardPage from '@/pages/DashboardPage';
import SupportStaffDashboard from '@/pages/SupportStaffDashboard';
import PortalHome from '@/pages/portal/PortalHome';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/legal/TermsOfServicePage';
import SmsTermsPage from '@/pages/legal/SmsTermsPage';
import SmsOptInPage from '@/pages/legal/SmsOptInPage';
import PricingPage from '@/pages/portal/PricingPage';
import ContactPage from '@/pages/portal/ContactPage';
import LoadingSpinner from '@/components/LoadingSpinner';
import TeacherLayout from '@/components/TeacherLayout';
import SuperAdminLayout from '@/components/SuperAdminLayout';
import { Profile } from '@/pages/Profile';
import { Sessions } from '@/pages/Sessions';

// Auth pages (critical path)
import SetPassword from '@/pages/auth/SetPassword';
import RequestPasswordReset from '@/pages/auth/RequestPasswordReset';
import ResetPassword from '@/pages/auth/ResetPassword';
import VerifyEmail from '@/pages/auth/VerifyEmail';

// Parent registration and invitation acceptance (public, needs to be fast)
import ParentRegistration from '@/pages/parent/ParentRegistration';
import ParentAcceptInvitation from '@/pages/parent/ParentAcceptInvitation';
import ParentRegistrationRedirect from '@/components/ParentRegistrationRedirect';
import { useParentLanguage } from '@/hooks/useParentLanguage';

// ============================================
// ADMIN PAGES - Lazy Loaded
// ============================================
// Configuration
const SchoolConfiguration = lazy(() => import('@/pages/admin/SchoolConfiguration'));

// Scheduling
const TimeSlots = lazy(() => import('@/pages/admin/TimeSlots'));
const SchoolYears = lazy(() => import('@/pages/admin/SchoolYears'));
const DayTypes = lazy(() => import('@/pages/admin/DayTypes'));
const SchoolCalendar = lazy(() => import('@/pages/admin/SchoolCalendar'));
const CalendarGenerationForm = lazy(() => import('@/pages/admin/CalendarGenerationForm'));

// Classes & Blocks
const Classes = lazy(() => import('@/pages/admin/Classes'));
const Blocks = lazy(() => import('@/pages/admin/Blocks'));
const Periods = lazy(() => import('@/pages/admin/Periods'));
const BulkMeetingPatternWizard = lazy(() => import('@/pages/admin/BulkMeetingPatternWizard'));
const CloneSchoolYearWizard = lazy(() => import('@/pages/admin/CloneSchoolYearWizard'));
const GradeProgressionTool = lazy(() => import('@/pages/admin/GradeProgressionTool'));

// Students & Parents
const Students = lazy(() => import('@/pages/admin/Students'));
const StudentsImport = lazy(() => import('@/pages/admin/StudentsImport'));
const BulkPhotoImport = lazy(() => import('@/pages/admin/BulkPhotoImport'));
const Parents = lazy(() => import('@/pages/admin/Parents'));
const ParentsImport = lazy(() => import('@/pages/admin/ParentsImport'));

// Staff
const Staff = lazy(() => import('@/pages/admin/Staff'));
const StaffImport = lazy(() => import('@/pages/admin/StaffImport'));
const StaffAssignments = lazy(() => import('@/pages/admin/StaffAssignments'));

// Behavior
const BehaviorCategories = lazy(() => import('@/pages/admin/BehaviorCategories'));
const LocationTypes = lazy(() => import('@/pages/admin/LocationTypes'));

// Lunch
const LunchGroups = lazy(() => import('@/pages/admin/LunchGroups'));
const LunchSchedules = lazy(() => import('@/pages/admin/LunchSchedules'));

// Messages
const Messages = lazy(() => import('@/pages/admin/Messages'));

// Utilities
const TenantAuditLogs = lazy(() => import('@/pages/admin/TenantAuditLogs'));
const SmsUsagePage = lazy(() => import('@/pages/admin/SmsUsagePage'));
const BulkQRCodes = lazy(() => import('@/pages/admin/BulkQRCodes'));
const AttendanceManagement = lazy(() => import('@/pages/admin/AttendanceManagement'));

// Reports
const ReportsDashboard = lazy(() => import('@/pages/admin/reports/ReportsDashboard'));
const BehaviorSummaryReport = lazy(() => import('@/pages/admin/reports/BehaviorSummaryReport'));
const StaffBehaviorLeaderboard = lazy(() => import('@/pages/admin/reports/StaffBehaviorLeaderboard'));
const StudentProgressReport = lazy(() => import('@/pages/admin/reports/StudentProgressReport'));
const AttendanceReport = lazy(() => import('@/pages/admin/reports/AttendanceReport'));

// ============================================
// SUPER ADMIN PAGES - Lazy Loaded
// ============================================
const SuperAdminDashboard = lazy(() => import('@/pages/admin/SuperAdminDashboard'));
const SuperAdminAuditLogs = lazy(() => import('@/pages/admin/SuperAdminAuditLogs'));
const SuperAdminTenantAuditLogs = lazy(() => import('@/pages/admin/SuperAdminTenantAuditLogs'));
const TenantsListPage = lazy(() => import('@/pages/admin/TenantsListPage'));
const TenantDetailsPage = lazy(() => import('@/pages/admin/TenantDetailsPage'));
const CreateTenantPage = lazy(() => import('@/pages/admin/CreateTenantPage'));
const CreateUserPage = lazy(() => import('@/pages/admin/CreateUserPage'));
const LogsPage = lazy(() => import('@/pages/admin/LogsPage'));
const NotificationTesting = lazy(() => import('@/pages/admin/NotificationTesting'));
const SupportTicketsAdmin = lazy(() => import('@/pages/admin/SupportTicketsAdmin'));
const SystemAnnouncementsPage = lazy(() => import('@/pages/admin/SystemAnnouncements'));
const NotificationUsagePage = lazy(() => import('@/pages/admin/NotificationUsagePage'));
const TenantNotificationUsageDetailPage = lazy(
  () => import('@/pages/admin/TenantNotificationUsageDetailPage')
);
const SuperAdminNotificationLogs = lazy(() => import('@/pages/admin/SuperAdminNotificationLogs'));
const EmailSuppression = lazy(() => import('@/pages/admin/EmailSuppression'));

// ============================================
// TEACHER PAGES - Lazy Loaded
// ============================================
const MyBlocksToday = lazy(() => import('@/pages/teacher/MyBlocksToday'));
const MyStudents = lazy(() => import('@/pages/teacher/MyStudents'));
const TeacherMessages = lazy(() => import('@/pages/teacher/TeacherMessages'));
const HelpPage = lazy(() => import('@/pages/HelpPage'));

// ============================================
// PARENT PORTAL - Lazy Loaded
// ============================================
const ParentDashboard = lazy(() => import('@/pages/parent/ParentDashboard'));
const ActivityFeed = lazy(() => import('@/pages/parent/ActivityFeed'));
const ScheduleView = lazy(() => import('@/pages/parent/ScheduleView'));
const MessagesView = lazy(() => import('@/pages/parent/MessagesView'));
const ParentNotificationSettings = lazy(() => import('@/pages/parent/NotificationSettings'));

// ============================================
// SUPPORT TICKETS - Lazy Loaded
// ============================================
const SubmitTicket = lazy(() => import('@/pages/support/SubmitTicket'));
const MyTickets = lazy(() => import('@/pages/support/MyTickets'));
const TicketDetail = lazy(() => import('@/pages/support/TicketDetail'));
const StaffMessages = lazy(() => import('@/pages/support/StaffMessages'));

/**
 * Syncs parent language preference from API to i18next.
 * Wraps parent Layout so the hook runs within a rendered component.
 */
function ParentLanguageSync({ children }: { children: React.ReactNode }) {
  useParentLanguage();
  return <>{children}</>;
}

function App() {
  const { initializeAuth, isLoading: authLoading, tenant, user, viewMode } = useAuthStore();
  const { initializeTheme, resolvedTheme } = useUIStore();
  const { publicConfig } = useSchoolTimezone();

  // Initialize theme on app load
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  // Detect if we're on the admin site
  const adminSite = isAdminSite();

  // Detect if we're on the portal site (no subdomain)
  const hostname = window.location.hostname;
  const isPortalSite =
    hostname === 'checkademic.com' ||
    hostname === 'www.checkademic.com' ||
    hostname === 'checkademic.dev' ||
    hostname === 'www.checkademic.dev' ||
    hostname === 'localhost';

  // Apply branding from TimezoneContext (which fetches public config)
  useEffect(() => {
    // Skip branding on portal site (no subdomain) or admin site
    if (isPortalSite || adminSite) return;

    // Apply branding from context (either from public endpoint or authStore)
    if (publicConfig?.branding) {
      applyBranding(publicConfig.branding);
    }
  }, [publicConfig, isPortalSite, adminSite]);

  useEffect(() => {
    // Skip entire auth initialization on portal site
    if (isPortalSite) return;

    // For admin site, initialize auth but skip tenant config
    if (adminSite) {
      initializeAuth();
      return;
    }

    // Check for cached tenant config
    const cached = localStorage.getItem('tenant_config');
    const cachedTime = localStorage.getItem('tenant_config_time');
    const MAX_AGE = 3600000; // 1 hour in milliseconds

    if (cached && cachedTime) {
      const age = Date.now() - Number(cachedTime);

      if (age < MAX_AGE) {
        // Cache is fresh - apply branding immediately
        try {
          const cachedTenant = JSON.parse(cached);
          applyBranding(cachedTenant.settings.branding);
        } catch (error) {
          console.error('Failed to parse cached config:', error);
          localStorage.removeItem('tenant_config');
          localStorage.removeItem('tenant_config_time');
        }
      } else {
        // Cache expired - clear it
        localStorage.removeItem('tenant_config');
        localStorage.removeItem('tenant_config_time');
      }
    }

    // Initialize auth state
    initializeAuth();
  }, [initializeAuth, isPortalSite, adminSite]);

  // Apply branding when tenant loads
  useEffect(() => {
    if (tenant?.settings.branding) {
      applyBranding(tenant.settings.branding);
    }
  }, [tenant]);

  // Re-apply branding when theme changes (to update danger color shades for dark/light mode)
  useEffect(() => {
    const branding = tenant?.settings.branding || publicConfig?.branding;
    if (branding) {
      applyBranding(branding);
    }
  }, [resolvedTheme, tenant?.settings.branding, publicConfig?.branding]);

  // Don't show loading spinner on portal site (no auth needed)
  if (authLoading && !isPortalSite) {
    return <LoadingSpinner />;
  }

  if (adminSite) {
    return (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LazyLoadFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />

            {/* Protected super-admin routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <SuperAdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuperAdminDashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="tenants" element={<TenantsListPage />} />
              <Route path="tenants/create" element={<CreateTenantPage />} />
              <Route path="tenants/:id" element={<TenantDetailsPage />} />
              <Route path="tenants/:tenantId/audit-logs" element={<SuperAdminTenantAuditLogs />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="users/create" element={<CreateUserPage />} />
              <Route path="super-admin/audit-logs" element={<SuperAdminAuditLogs />} />
              <Route path="notification-testing" element={<NotificationTesting />} />
              <Route path="support-tickets" element={<SupportTicketsAdmin />} />
              <Route path="announcements" element={<SystemAnnouncementsPage />} />
              <Route path="usage" element={<NotificationUsagePage />} />
              <Route path="usage/:tenantId" element={<TenantNotificationUsageDetailPage />} />
              <Route path="notification-logs" element={<SuperAdminNotificationLogs />} />
              <Route path="email-suppression" element={<EmailSuppression />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    );
  }

  if (isPortalSite) {
    return (
      <Routes>
        {/* Portal site routes (no subdomain) */}
        <Route path="/" element={<PortalHome />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/sms-terms" element={<SmsTermsPage />} />
        <Route path="/sms-opt-in" element={<SmsOptInPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Show 404 for unknown routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    );
  }

  /**
   * TENANT SITE - Role-based route trees
   *
   * Teachers and admins get completely separate route configurations.
   * This pattern ensures:
   * - Clean separation of concerns
   * - Proper React Router Outlet context
   * - No component remounting on role changes
   * - Route protection (teachers can't access /admin/*)
   *
   * Architecture matches existing admin site vs portal site separation.
   */

  // Teacher route tree (Phase 5 - unified-calendar-block-architecture)
  if (user?.role === 'teacher') {
    return (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LazyLoadFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            {/* Legacy URL redirect - can be removed after 6/1/2026 */}
            <Route path="/parent-registration" element={<ParentRegistrationRedirect />} />
            <Route path="/parent/register" element={<ParentRegistration />} />
            <Route path="/parent/accept-invitation" element={<ParentAcceptInvitation />} />

            {/* Protected teacher routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <TeacherLayout />
                </ProtectedRoute>
              }
            >
              {/* Phase 5: Enhanced MyBlocksToday dashboard with student photos and behavior indicators */}
              <Route index element={<MyBlocksToday />} />
              <Route path="my-students" element={<MyStudents />} />
              <Route path="messages" element={
                <FeatureModuleGuard module="messaging" fallback={<ModuleNotAvailable />}>
                  <TeacherMessages />
                </FeatureModuleGuard>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="help" element={<HelpPage />} />

              {/* Reports */}
              <Route path="reports/student-progress" element={<StudentProgressReport />} />

              {/* Support Tickets */}
              <Route path="support" element={<MyTickets />} />
              <Route path="support/new" element={<SubmitTicket />} />
              <Route path="support/:id" element={<TicketDetail />} />

              {/* Catch-all: redirect teachers trying to access /admin/* (no 403) */}
              <Route path="admin/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch-all: show 404 for unknown routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    );
  }

  // Support staff route tree (stub for Phase 3+)
  if (user?.role === 'support_staff') {
    return (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LazyLoadFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            {/* Legacy URL redirect - can be removed after 6/1/2026 */}
            <Route path="/parent-registration" element={<ParentRegistrationRedirect />} />
            <Route path="/parent/register" element={<ParentRegistration />} />
            <Route path="/parent/accept-invitation" element={<ParentAcceptInvitation />} />

            {/* Protected support staff routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SupportStaffDashboard />} />
              <Route path="messages" element={
                <FeatureModuleGuard module="messaging" fallback={<ModuleNotAvailable />}>
                  <StaffMessages />
                </FeatureModuleGuard>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="help" element={<HelpPage />} />

              {/* Support Tickets */}
              <Route path="support" element={<MyTickets />} />
              <Route path="support/new" element={<SubmitTicket />} />
              <Route path="support/:id" element={<TicketDetail />} />

              {/* Catch-all: redirect support staff trying to access /admin/* (no 403) */}
              <Route path="admin/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch-all: show 404 for unknown routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    );
  }

  // Parent route tree (Parent Portal V1)
  if (user?.role === 'parent') {
    return (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LazyLoadFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            {/* Legacy URL redirect - can be removed after 6/1/2026 */}
            <Route path="/parent-registration" element={<ParentRegistrationRedirect />} />
            <Route path="/parent/register" element={<ParentRegistration />} />
            <Route path="/parent/accept-invitation" element={<ParentAcceptInvitation />} />

            {/* Protected parent routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ParentLanguageSync>
                    <Layout />
                  </ParentLanguageSync>
                </ProtectedRoute>
              }
            >
              <Route index element={<ParentDashboard />} />
              <Route path="activity" element={<ActivityFeed />} />
              <Route path="messages" element={
                <FeatureModuleGuard module="messaging" fallback={<ModuleNotAvailable />}>
                  <MessagesView />
                </FeatureModuleGuard>
              } />
              <Route path="schedule" element={<ScheduleView />} />
              <Route path="profile" element={<Profile />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="settings" element={<ParentNotificationSettings />} />
              <Route path="help" element={<HelpPage />} />

              {/* Support Tickets */}
              <Route path="support" element={<MyTickets />} />
              <Route path="support/new" element={<SubmitTicket />} />
              <Route path="support/:id" element={<TicketDetail />} />

              {/* Catch-all: redirect parents trying to access /admin/* (no 403) */}
              <Route path="admin/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch-all: show 404 for unknown routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    );
  }

  // Admin in Teacher Mode - render teacher route tree with admin privileges
  if (user?.role === 'admin' && viewMode === 'teacher') {
    return (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LazyLoadFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            {/* Legacy URL redirect - can be removed after 6/1/2026 */}
            <Route path="/parent-registration" element={<ParentRegistrationRedirect />} />
            <Route path="/parent/register" element={<ParentRegistration />} />
            <Route path="/parent/accept-invitation" element={<ParentAcceptInvitation />} />

            {/* Protected teacher routes (admin using teacher UI) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <TeacherLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<MyBlocksToday />} />
              <Route path="my-students" element={<MyStudents />} />
              <Route path="messages" element={
                <FeatureModuleGuard module="messaging" fallback={<ModuleNotAvailable />}>
                  <TeacherMessages />
                </FeatureModuleGuard>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="help" element={<HelpPage />} />

              {/* Reports */}
              <Route path="reports/student-progress" element={<StudentProgressReport />} />

              {/* Support Tickets */}
              <Route path="support" element={<MyTickets />} />
              <Route path="support/new" element={<SubmitTicket />} />
              <Route path="support/:id" element={<TicketDetail />} />

              {/* Redirect admin URLs to home when in teacher mode */}
              <Route path="admin/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    );
  }

  // Admin in Support Staff Mode - render support staff route tree with admin privileges
  if (user?.role === 'admin' && viewMode === 'support_staff') {
    return (
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LazyLoadFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            {/* Legacy URL redirect - can be removed after 6/1/2026 */}
            <Route path="/parent-registration" element={<ParentRegistrationRedirect />} />
            <Route path="/parent/register" element={<ParentRegistration />} />
            <Route path="/parent/accept-invitation" element={<ParentAcceptInvitation />} />

            {/* Protected support staff routes (admin using support staff UI) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SupportStaffDashboard />} />
              <Route path="messages" element={
                <FeatureModuleGuard module="messaging" fallback={<ModuleNotAvailable />}>
                  <StaffMessages />
                </FeatureModuleGuard>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="help" element={<HelpPage />} />

              {/* Support Tickets */}
              <Route path="support" element={<MyTickets />} />
              <Route path="support/new" element={<SubmitTicket />} />
              <Route path="support/:id" element={<TicketDetail />} />

              {/* Redirect admin URLs to home when in support staff mode */}
              <Route path="admin/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LazyLoadErrorBoundary>
    );
  }

  // Admin/super_admin route tree (existing - unchanged)
  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<LazyLoadFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/sms-terms" element={<SmsTermsPage />} />
          <Route path="/sms-opt-in" element={<SmsOptInPage />} />
          <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/set-password" element={<SetPassword />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />
          {/* Legacy URL redirect - can be removed after 6/1/2026 */}
          <Route path="/parent-registration" element={<ParentRegistrationRedirect />} />
          <Route path="/parent/register" element={<ParentRegistration />} />
          <Route path="/parent/accept-invitation" element={<ParentAcceptInvitation />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="sessions" element={<Sessions />} />
            <Route path="admin/configuration" element={<SchoolConfiguration />} />
            <Route path="admin/school-years" element={<SchoolYears />} />
            <Route path="admin/school-years/clone" element={<CloneSchoolYearWizard />} />
            <Route path="admin/day-types" element={<DayTypes />} />
            <Route path="admin/time-slots" element={<TimeSlots />} />
            <Route path="admin/calendar" element={<SchoolCalendar />} />
            <Route path="admin/calendar-generation" element={<CalendarGenerationForm />} />
            {/* Dual-Mode UI: Simplified class/student management */}
            <Route path="admin/periods" element={<Periods />} />
            <Route path="admin/lunch-groups" element={<LunchGroups />} />
            <Route path="admin/lunch-schedules" element={<LunchSchedules />} />
            <Route path="admin/students" element={<Students />} />
            <Route path="admin/students/import" element={<StudentsImport />} />
            <Route path="admin/students/photos/import" element={<BulkPhotoImport />} />
            <Route path="admin/students/grade-progression" element={<GradeProgressionTool />} />
            {/* Advanced Scheduling: Block-level scheduling */}
            <Route path="admin/classes" element={<Classes />} />
            <Route path="admin/blocks" element={<Blocks />} />
            <Route path="admin/bulk-meeting-patterns" element={<BulkMeetingPatternWizard />} />
            <Route path="admin/parents" element={
              <FeatureModuleGuard module="parentPortal" fallback={<ModuleNotAvailable />}>
                <Parents />
              </FeatureModuleGuard>
            } />
            <Route path="admin/parents/import" element={
              <FeatureModuleGuard module="parentPortal" fallback={<ModuleNotAvailable />}>
                <ParentsImport />
              </FeatureModuleGuard>
            } />
            <Route path="admin/staff-assignments" element={<StaffAssignments />} />
            <Route path="admin/staff" element={<Staff />} />
            <Route path="admin/staff/import" element={<StaffImport />} />
            <Route path="admin/behavior-categories" element={<BehaviorCategories />} />
            <Route path="admin/location-types" element={<LocationTypes />} />
            <Route path="admin/bulk-qr-codes" element={
              <FeatureModuleGuard module="parentPortal" fallback={<ModuleNotAvailable />}>
                <BulkQRCodes />
              </FeatureModuleGuard>
            } />
            <Route path="admin/messages" element={
              <FeatureModuleGuard module="messaging" fallback={<ModuleNotAvailable />}>
                <Messages />
              </FeatureModuleGuard>
            } />
            <Route path="admin/audit-logs" element={<TenantAuditLogs />} />
            <Route path="admin/sms-usage" element={
              <FeatureModuleGuard module="sms" layer="allowed" fallback={<ModuleNotAvailable />}>
                <SmsUsagePage />
              </FeatureModuleGuard>
            } />
            <Route path="admin/attendance" element={
              <FeatureModuleGuard module="attendance" fallback={<ModuleNotAvailable />}>
                <AttendanceManagement />
              </FeatureModuleGuard>
            } />
            {/* Reports */}
            <Route path="admin/reports" element={<ReportsDashboard />} />
            <Route path="admin/reports/behavior-summary" element={<BehaviorSummaryReport />} />
            <Route path="admin/reports/staff-behavior-leaderboard" element={<StaffBehaviorLeaderboard />} />
            <Route path="admin/reports/student-progress" element={<StudentProgressReport />} />
            <Route path="admin/reports/attendance" element={
              <FeatureModuleGuard module="attendance" fallback={<ModuleNotAvailable />}>
                <AttendanceReport />
              </FeatureModuleGuard>
            } />

            {/* Help */}
            <Route path="help" element={<HelpPage />} />

            {/* Support Tickets */}
            <Route path="support" element={<MyTickets />} />
            <Route path="support/new" element={<SubmitTicket />} />
            <Route path="support/:id" element={<TicketDetail />} />

            {/* Catch-all: show 404 for unknown routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </LazyLoadErrorBoundary>
  );
}

export default App;
